#!/usr/bin/env ts-node

/**
 * GTD Data Seeding Script
 * 
 * Creates consistent test data for GTD domain across all environments.
 * Follows David Allen's GTD methodology with proper DDD patterns.
 * Uses the same robust pattern as Party Manager and UX Design.
 */

import { getMongoClient, getMongoConfig, initializeMongoConnection } from "@swoft/persistence";
import { GTD_COLLECTIONS } from '../config/collections';
import { v4 as uuidv4 } from 'uuid';

interface GTDTestData {
  organizationId: string;
  personId: string;
  inboxItems: Array<{
    content: string;
    source: string;
    priority: string;
    isProcessed: boolean;
  }>;
  projects: Array<{
    title: string;
    description: string;
    desiredOutcome: string;
    status: string;
    priority: string;
  }>;
  nextActions: Array<{
    description: string;
    context: string;
    energyLevel: string;
    estimatedMinutes: number;
    projectId?: string;
    status: string;
  }>;
  referenceItems: Array<{
    title: string;
    content: string;
    category: string;
    tags: string[];
  }>;
}

class GTDDataSeeder {
  private testData: GTDTestData = {
    organizationId: '2484554d-85d3-49a4-87bf-ceded4b115c0',
    personId: '52149abb-d080-4b1c-8283-8d603b9ca44d', // Derick's person ID
    inboxItems: [
      {
        content: '🌱 UAT SEEDED DATA - This GTD inbox was populated with test data for development/testing purposes',
        source: 'system',
        priority: 'low',
        isProcessed: false
      }
    ],
    projects: [
      {
        title: 'Swoft Platform DevOps Enhancement',
        description: 'Improve deployment processes, monitoring, and data integrity across all services',
        desiredOutcome: 'Zero-downtime deployments with automated validation and rollback capabilities',
        status: 'active',
        priority: 'high'
      },
      {
        title: 'AI Agent Framework Integration',
        description: 'Integrate AI agents with human workflows for enhanced productivity',
        desiredOutcome: 'Seamless AI-human collaboration with clear task delegation and monitoring',
        status: 'active',
        priority: 'high'
      },
      {
        title: 'Domain Package Standardization',
        description: 'Apply consistent DevOps patterns across all domain packages',
        desiredOutcome: 'All domain packages have seeding scripts, data validation, and deployment checklists',
        status: 'active',
        priority: 'medium'
      }
    ],
    nextActions: [
      {
        description: 'Create data seeding script for business-strategy domain',
        context: '@computer',
        energyLevel: 'medium',
        estimatedMinutes: 45,
        status: 'available'
      },
      {
        description: 'Call Kevin to discuss AI agent roadmap priorities',
        context: '@calls',
        energyLevel: 'high',
        estimatedMinutes: 30,
        status: 'available'
      },
      {
        description: 'Review and approve product design mockups',
        context: '@computer',
        energyLevel: 'medium',
        estimatedMinutes: 20,
        status: 'available'
      },
      {
        description: 'Test new deployment pipeline on staging environment',
        context: '@computer',
        energyLevel: 'high',
        estimatedMinutes: 60,
        status: 'available'
      },
      {
        description: 'Pick up new development books from bookstore',
        context: '@errands',
        energyLevel: 'low',
        estimatedMinutes: 15,
        status: 'available'
      }
    ],
    referenceItems: [
      {
        title: 'MongoDB Performance Optimization Guide',
        content: 'Best practices for database indexing, query optimization, and connection pooling',
        category: 'technical',
        tags: ['mongodb', 'performance', 'database']
      },
      {
        title: 'David Allen GTD Methodology Summary',
        content: 'Core principles: Capture, Clarify, Organize, Reflect, Engage',
        category: 'productivity',
        tags: ['gtd', 'methodology', 'productivity']
      },
      {
        title: 'AI Agent Integration Patterns',
        content: 'Common patterns for integrating AI agents with human workflows',
        category: 'ai',
        tags: ['ai', 'patterns', 'integration']
      },
      {
        title: 'Domain-Driven Design Principles',
        content: 'Eric Evans DDD concepts: bounded contexts, aggregates, entities, value objects',
        category: 'architecture',
        tags: ['ddd', 'architecture', 'design']
      }
    ]
  };

  private async setupMongo(): Promise<void> {
    const mongoConfig = {
      uri: process.env.MONGODB_URL || 'mongodb://localhost:27017',
      dbName: process.env.MONGODB_DB_NAME || 'swoft_designer_dev'
    };
    
    console.log(`🔗 Connecting to database: ${mongoConfig.dbName}`);
    await initializeMongoConnection(mongoConfig);
  }

  async run(): Promise<void> {
    try {
      console.log('🚀 Starting GTD Data Seeding...\n');
      
      await this.setupMongo();
      
      // Step 1: Clear existing data
      await this.clearExistingData();
      
      // Step 2: Seed inbox items
      await this.seedInboxItems();
      
      // Step 3: Seed projects
      const projectIds = await this.seedProjects();
      
      // Step 4: Seed next actions (link some to projects)
      await this.seedNextActions(projectIds);
      
      // Step 5: Seed reference items
      await this.seedReferenceItems();
      
      // Step 6: Verify data integrity
      await this.verifyDataIntegrity();
      
      console.log('\n✅ GTD Data Seeding Completed Successfully!');
      console.log(`📊 Organization: ${this.testData.organizationId}`);
      console.log(`👤 Person: ${this.testData.personId}`);
      console.log(`📥 Inbox Items: ${this.testData.inboxItems.length} created`);
      console.log(`📋 Projects: ${this.testData.projects.length} created`);
      console.log(`⚡ Next Actions: ${this.testData.nextActions.length} created`);
      console.log(`📚 Reference Items: ${this.testData.referenceItems.length} created`);
      
    } catch (error) {
      console.error('❌ GTD seeding failed:', error);
      throw error;
    }
  }

  private async clearExistingData(): Promise<void> {
    console.log('🧹 Clearing existing GTD data...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    // Clear inbox items for this person
    await db.collection(GTD_COLLECTIONS.INBOX_ITEMS).deleteMany({
      capturedByPersonId: this.testData.personId
    });
    
    // Clear projects for this person
    await db.collection(GTD_COLLECTIONS.PROJECTS).deleteMany({
      ownerId: this.testData.personId
    });
    
    // Clear next actions for this person
    await db.collection(GTD_COLLECTIONS.NEXT_ACTIONS).deleteMany({
      assignedToPersonId: this.testData.personId
    });
    
    // Clear reference items for this person
    await db.collection(GTD_COLLECTIONS.REFERENCE_ITEMS).deleteMany({
      ownerId: this.testData.personId
    });
    
    console.log('✅ Existing data cleared');
  }

  private async seedInboxItems(): Promise<void> {
    console.log('\n📥 Seeding inbox items...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    // Add seeding timestamp to the first item dynamically
    const itemsToSeed = [...this.testData.inboxItems];
    itemsToSeed[0].content = `🌱 UAT SEEDED DATA - This GTD inbox was populated with test data for development/testing purposes on ${new Date().toISOString()}`;
    
    for (const itemData of itemsToSeed) {
      const itemId = uuidv4();
      
      const inboxItem = {
        itemId: itemId,
        id: itemId,
        content: itemData.content,
        source: itemData.source,
        priority: itemData.priority,
        isProcessed: itemData.isProcessed,
        capturedByPersonId: this.testData.personId,
        organizationId: this.testData.organizationId,
        capturedAt: new Date(),
        processedAt: null,
        processedByPersonId: null,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await db.collection(GTD_COLLECTIONS.INBOX_ITEMS).insertOne(inboxItem);
      
      console.log(`✅ Created inbox item: ${itemData.content.substring(0, 50)}...`);
    }
  }

  private async seedProjects(): Promise<{ [title: string]: string }> {
    console.log('\n📋 Seeding projects...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    const projectIds: { [title: string]: string } = {};
    
    for (const projectData of this.testData.projects) {
      const projectId = uuidv4();
      
      const project = {
        projectId: projectId,
        id: projectId,
        title: projectData.title,
        description: projectData.description,
        desiredOutcome: projectData.desiredOutcome,
        status: projectData.status,
        priority: projectData.priority,
        ownerId: this.testData.personId,
        organizationId: this.testData.organizationId,
        startDate: new Date(),
        targetDate: null,
        completedDate: null,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await db.collection(GTD_COLLECTIONS.PROJECTS).insertOne(project);
      projectIds[projectData.title] = projectId;
      
      console.log(`✅ Created project: ${projectData.title}`);
    }
    
    return projectIds;
  }

  private async seedNextActions(projectIds: { [title: string]: string }): Promise<void> {
    console.log('\n⚡ Seeding next actions...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    for (const [index, actionData] of this.testData.nextActions.entries()) {
      const actionId = uuidv4();
      
      // Link first action to first project
      const linkedProjectId = index === 0 ? Object.values(projectIds)[0] : null;
      
      const nextAction = {
        actionId: actionId,
        id: actionId,
        description: actionData.description,
        context: actionData.context,
        energyLevel: actionData.energyLevel,
        estimatedMinutes: actionData.estimatedMinutes,
        projectId: linkedProjectId,
        status: actionData.status,
        assignedToPersonId: this.testData.personId,
        organizationId: this.testData.organizationId,
        dueDate: null,
        completedDate: null,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await db.collection(GTD_COLLECTIONS.NEXT_ACTIONS).insertOne(nextAction);
      
      console.log(`✅ Created next action: ${actionData.description.substring(0, 50)}...`);
    }
  }

  private async seedReferenceItems(): Promise<void> {
    console.log('\n📚 Seeding reference items...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    for (const refData of this.testData.referenceItems) {
      const refId = uuidv4();
      
      const referenceItem = {
        referenceId: refId,
        id: refId,
        title: refData.title,
        content: refData.content,
        category: refData.category,
        tags: refData.tags,
        ownerId: this.testData.personId,
        organizationId: this.testData.organizationId,
        isArchived: false,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await db.collection(GTD_COLLECTIONS.REFERENCE_ITEMS).insertOne(referenceItem);
      
      console.log(`✅ Created reference item: ${refData.title}`);
    }
  }

  private async verifyDataIntegrity(): Promise<void> {
    console.log('\n🧪 Verifying data integrity...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    // Verify inbox items
    const inboxCount = await db.collection(GTD_COLLECTIONS.INBOX_ITEMS)
      .countDocuments({ capturedByPersonId: this.testData.personId });
    console.log(inboxCount === 5 ? '✅ Inbox items verified (5 created)' : `❌ Inbox items mismatch (expected 5, got ${inboxCount})`);
    
    // Verify projects
    const projectCount = await db.collection(GTD_COLLECTIONS.PROJECTS)
      .countDocuments({ ownerId: this.testData.personId });
    console.log(projectCount === 3 ? '✅ Projects verified (3 created)' : `❌ Projects mismatch (expected 3, got ${projectCount})`);
    
    // Verify next actions
    const actionCount = await db.collection(GTD_COLLECTIONS.NEXT_ACTIONS)
      .countDocuments({ assignedToPersonId: this.testData.personId });
    console.log(actionCount === 5 ? '✅ Next actions verified (5 created)' : `❌ Next actions mismatch (expected 5, got ${actionCount})`);
    
    // Verify reference items
    const refCount = await db.collection(GTD_COLLECTIONS.REFERENCE_ITEMS)
      .countDocuments({ ownerId: this.testData.personId });
    console.log(refCount === 4 ? '✅ Reference items verified (4 created)' : `❌ Reference items mismatch (expected 4, got ${refCount})`);
    
    console.log('\n📊 Database Summary:');
    console.log(`Database: ${config?.dbName}`);
    console.log(`Collections: ${Object.values(GTD_COLLECTIONS).join(', ')}`);
    console.log(`Person ID: ${this.testData.personId}`);
    console.log(`Organization ID: ${this.testData.organizationId}`);
  }
}

// Execute if run directly (ESM compatible)
if (import.meta.url === `file://${process.argv[1]}`) {
  const seeder = new GTDDataSeeder();
  seeder.run()
    .then(() => {
      console.log('\n🎉 GTD data is ready!');
      console.log('\n📋 GTD System Summary:');
      console.log('• 5 inbox items ready for processing');
      console.log('• 3 active projects with clear outcomes');
      console.log('• 5 next actions organized by context');
      console.log('• 4 reference items for future use');
      console.log('\n🚀 Ready to implement Getting Things Done methodology!');
      process.exit(0);
    })
    .catch((error) => {
      console.error('\n💥 GTD seeding failed:', error);
      process.exit(1);
    });
}

export { GTDDataSeeder };